import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';

import { trackByValue } from '@recruitee/common';
import type {
  EmailTemplate,
  EmailTemplatesEventInvitation,
} from '@recruitee/email-templates-types';
import { GdprService } from '@recruitee/gdpr-data';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import {
  ConnectedOverlayDirective,
  DropdownComponent,
  IconName,
  KeyCode,
  NestedTemplateTriggerDirective,
  DropdownHeaderComponent,
  ScrollableDirective,
} from '@recruitee/user-interface';
import { asViewRef } from '@recruitee/utils';

import { RedactorInsertsStep } from '../../../redactor/types';
import { WysiwygLink } from '../../../wysiwyg-extensions/link/wysiwyg-link.types';
import { RedactorInsertsDropdownItemComponent } from '../redactor-inserts-dropdown-item/redactor-inserts-dropdown-item.component';
import {
  InsertsEmailKind,
  RedactorInsertsEmailTemplatesComponent,
} from '../redactor-inserts-email-templates/redactor-inserts-email-templates.component';
import { RedactorInsertsGdprComponent } from '../redactor-inserts-gdpr/redactor-inserts-gdpr.component';
import { RedactorInsertsIntegrationsKununuComponent } from '../redactor-inserts-integrations-kununu/redactor-inserts-integrations-kununu.component';
import { RedactorInsertsQuestionnairesComponent } from '../redactor-inserts-questionnaires/redactor-inserts-questionnaires.component';
import { RedactorInsertsSchedulerComponent } from '../redactor-inserts-scheduler/redactor-inserts-scheduler.component';

@Component({
  selector: 'rt-redactor-inserts-dropdown',
  templateUrl: 'redactor-inserts-dropdown.component.html',
  styleUrls: ['./redactor-inserts-dropdown.component.less'],
  host: {
    '(window:keydown)': 'handleKeyDown($event)',
  },
  standalone: true,
  imports: [
    DropdownComponent,
    ScrollableDirective,
    DropdownHeaderComponent,
    RedactorInsertsDropdownItemComponent,
    NestedTemplateTriggerDirective,
    RedactorInsertsQuestionnairesComponent,
    RedactorInsertsEmailTemplatesComponent,
    RedactorInsertsSchedulerComponent,
    RedactorInsertsGdprComponent,
    RedactorInsertsIntegrationsKununuComponent,
    TranslatePipe,
  ],
})
export class RedactorInsertsDropdownComponent {
  @Input() public inserts: RedactorInsertsStep[];
  @Input() public emailTemplateInsideTemplate?: TemplateRef<void>;
  @Input() public dropdownRef: ConnectedOverlayDirective;
  @Input() public canManageCandidates: boolean;

  @Output() public emailTemplateChoose: EventEmitter<
    EmailTemplate | EmailTemplatesEventInvitation
  > = new EventEmitter();
  @Output() public insertLink: EventEmitter<WysiwygLink> = new EventEmitter();
  @Output() public saveAsTemplate = new EventEmitter<void>();

  @ViewChild('emailTemplates', { static: true }) public emailTemplates: TemplateRef<void>;
  @ViewChild('gdprLinks', { static: true }) public gdprLinks: TemplateRef<void>;
  @ViewChild('gdprLinksWithoutConsent', { static: true })
  public gdprLinksWithoutConsent: TemplateRef<void>;
  @ViewChild('questionnairesTemplates', { static: true })
  public questionnairesTemplates: TemplateRef<void>;
  @ViewChild('schedules', { static: true })
  public schedules: TemplateRef<void>;
  @ViewChild('eventInvitationTemplates', { static: true })
  public eventInvitationTemplates: TemplateRef<void>;
  @ViewChild('integrationsKununu', { static: true })
  public integrationsKununu: TemplateRef<void>;
  @ViewChild('dropdown', { static: true }) public dropdown: DropdownComponent;

  public selectedIndex: number | null = null;
  public readonly InsertsEmailKind = InsertsEmailKind;

  public RedactorInsertStepTemplateMap: Record<
    RedactorInsertsStep,
    { template: TemplateRef<void>; icon: IconName; heading: string; subheading: string }
  >;
  public trackByValue = trackByValue;
  public hasBackAction: boolean;

  constructor(
    private i18n: I18nService,
    private cdRef: ChangeDetectorRef,
    private router: Router,
    private gdprSvc: GdprService,
  ) {}

  public ngOnInit(): void {
    this.hasBackAction = this.inserts.length !== 1;
    this.setTemplateMap();
  }

  public ngAfterViewInit(): void {
    const hasFixedStep = this.inserts.length === 1;

    if (hasFixedStep) {
      this.setStep(this.inserts[0]);
    }
  }

  // eslint-disable-next-line max-lines-per-function
  public handleKeyDown(event: KeyboardEvent): void {
    const key = event.which || event.keyCode;

    switch (key) {
      case KeyCode.Escape:
        this.closeDropdown();
        break;

      case KeyCode.Enter:
        event.preventDefault();
        event.stopPropagation();
        if (this.selectedIndex === null) {
          this.closeDropdown();
        } else {
          this.setStep(this.inserts[this.selectedIndex]);
        }
        break;

      case KeyCode.UpArrow:
        event.preventDefault();
        if (this.selectedIndex === 0 || this.selectedIndex === null) {
          this.selectedIndex = this.inserts.length - 1;
        } else {
          this.selectedIndex = this.selectedIndex - 1;
        }
        this.cdRef.detectChanges();
        break;

      case KeyCode.DownArrow:
        event.preventDefault();
        if (this.selectedIndex === this.inserts.length - 1 || this.selectedIndex === null) {
          this.selectedIndex = 0;
        } else {
          this.selectedIndex = this.selectedIndex + 1;
        }
        this.cdRef.detectChanges();
        break;

      default:
        return;
    }
    if (!asViewRef(this.cdRef).destroyed) {
      this.cdRef.detectChanges();
    }
  }

  public isSelected(insert: RedactorInsertsStep): boolean {
    return this.inserts.indexOf(insert) === this.selectedIndex;
  }

  public setStep(step: RedactorInsertsStep): void {
    this.dropdown.setNestedTemplate(this.RedactorInsertStepTemplateMap[step].template);
    this.selectedIndex = null;
    this.cdRef.detectChanges();
  }

  public goToSettings(url: string): void {
    this.router.navigate([url]);
  }

  public closeDropdown(): void {
    this.dropdownRef.open = false;
  }

  // eslint-disable-next-line max-lines-per-function
  private setTemplateMap() {
    this.RedactorInsertStepTemplateMap = {
      'email-templates': {
        template: this.emailTemplates,
        icon: 'mail',
        heading: this.i18n.translate('app.redactor_inserts.email_templates.dropdown.heading'),
        subheading: this.i18n.translate('app.redactor_inserts.email_templates.dropdown.subheading'),
      },
      'gdpr-links': {
        template: this.gdprLinks,
        icon: 'gdpr-candidate',
        heading: this.i18n.translate('app.redactor_inserts.gdpr.dropdown.heading'),
        subheading: this.i18n.translate('app.redactor_inserts.gdpr.dropdown.subheading'),
      },
      'gdpr-links-without-consent': {
        template: this.gdprLinksWithoutConsent,
        icon: 'gdpr-candidate',
        heading: this.i18n.translate('app.redactor_inserts.gdpr.dropdown.heading'),
        subheading: this.i18n.translate('app.redactor_inserts.gdpr.dropdown.subheading'),
      },
      'questionnaires-templates': {
        template: this.questionnairesTemplates,
        icon: 'list',
        heading: this.i18n.translate('app.redactor_inserts.questionnaires.dropdown.heading'),
        subheading: this.i18n.translate('app.redactor_inserts.questionnaires.dropdown.subheading'),
      },
      schedules: {
        template: this.schedules,
        icon: 'access-time',
        heading: this.i18n.translate('app.redactor_inserts.scheduler.dropdown.heading'),
        subheading: this.i18n.translate('app.redactor_inserts.scheduler.dropdown.subheading'),
      },
      'event-invitation-templates': {
        template: this.eventInvitationTemplates,
        icon: 'mail',
        heading: this.i18n.translate('app.redactor_inserts.email_templates.dropdown.heading'),
        subheading: this.i18n.translate('app.redactor_inserts.email_templates.dropdown.subheading'),
      },
      'integrations-kununu': {
        template: this.integrationsKununu,
        icon: 'rti-kununu',
        heading: this.i18n.translate('app.redactor_inserts.integrations_kununu.dropdown.heading'),
        subheading: this.i18n.translate(
          'app.redactor_inserts.integrations_kununu.dropdown.subheading',
        ),
      },
    };
  }
}
